import React, { useCallback, useEffect, useState } from "react";
import { api, fmtDate, formatApiErrorDetail } from "@/lib/apiClient";
import { PageHeader, EmptyState } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

function Field({ label, children }) {
  return <div><Label className="label-caps mb-2 block">{label}</Label>{children}</div>;
}

const FREQ_LABEL = { monthly: "Monthly", quarterly: "Quarterly", halfyearly: "Half-yearly", yearly: "Yearly" };

export default function PPMSchedules({ projectId, embedded = false }) {
  const [rows, setRows] = useState([]);
  const [projects, setProjects] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [open, setOpen] = useState(false);
  const empty = {
    equipment_id: "", project_id: projectId || "", title: "",
    frequency: "quarterly", start_date: new Date().toISOString().slice(0, 10),
    assignee: "", active: true, notes: "",
  };
  const [form, setForm] = useState(empty);

  const load = useCallback(async () => {
    const [a, b, c] = await Promise.all([
      api.get("/ppm-schedules", { params: projectId ? { project_id: projectId } : {} }),
      api.get("/projects"),
      api.get("/equipment"),
    ]);
    setRows(a.data); setProjects(b.data); setEquipment(c.data);
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const projMap = Object.fromEntries(projects.map((p) => [p.id, p.name]));
  const eqMap = Object.fromEntries(equipment.map((e) => [e.id, `${e.tag_no || ""} ${e.name}`.trim()]));

  const submit = async () => {
    try {
      await api.post("/ppm-schedules", {
        ...form,
        equipment_id: form.equipment_id || null,
        project_id: form.project_id || projectId || null,
      });
      toast.success("PPM schedule created");
      setOpen(false); setForm(empty); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const complete = async (id) => {
    try {
      await api.post(`/ppm-schedules/${id}/complete`);
      toast.success("Visit logged; next due date advanced");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this PPM schedule?")) return;
    try { await api.delete(`/ppm-schedules/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const todayIso = new Date().toISOString().slice(0, 10);
  const in30 = new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10);
  const dueTone = (nextDue) => {
    if (!nextDue) return "";
    if (nextDue < todayIso) return "text-red-700 font-semibold";
    if (nextDue <= in30) return "text-amber-700 font-semibold";
    return "text-slate-700";
  };

  const formSheet = (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button data-testid="create-ppm-button" size={embedded ? "sm" : "default"} className="rounded-sm bg-[#EA580C] hover:bg-[#c2410c] text-white">
          <Plus size={14} className="mr-1" /> New PPM
        </Button>
      </SheetTrigger>
      <SheetContent className="w-[520px] sm:max-w-lg overflow-y-auto">
        <SheetHeader><SheetTitle className="font-display">Preventive Maintenance</SheetTitle></SheetHeader>
        <div className="space-y-3 mt-6">
          <Field label="Title"><Input data-testid="ppm-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Chiller Quarterly Inspection" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Frequency">
              <Select value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(FREQ_LABEL).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Start date"><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {!projectId && (
              <Field label="Project">
                <Select value={form.project_id || "__none__"} onValueChange={(v) => setForm({ ...form, project_id: v === "__none__" ? "" : v })}>
                  <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">None</SelectItem>
                    {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            )}
            <Field label="Equipment">
              <Select value={form.equipment_id || "__none__"} onValueChange={(v) => setForm({ ...form, equipment_id: v === "__none__" ? "" : v })}>
                <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">None</SelectItem>
                  {equipment.map((e) => <SelectItem key={e.id} value={e.id}>{e.tag_no} {e.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          </div>
          <Field label="Assignee"><Input value={form.assignee} onChange={(e) => setForm({ ...form, assignee: e.target.value })} placeholder="Ravi Kumar" /></Field>
          <Field label="Notes"><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
          <Button onClick={submit} data-testid="save-ppm-button" className="w-full rounded-sm bg-[#0F172A] hover:bg-slate-800">Create schedule</Button>
        </div>
      </SheetContent>
    </Sheet>
  );

  return (
    <div data-testid="ppm-page">
      {!embedded && (
        <PageHeader
          title="Preventive Maintenance"
          subtitle="Recurring maintenance schedules per equipment / site"
          module="Service · SV"
          actions={formSheet}
        />
      )}
      {embedded && (
        <div className="flex items-center justify-between mb-4">
          <div className="label-caps">PPM schedules ({rows.length})</div>
          {formSheet}
        </div>
      )}

      <div className={embedded ? "" : "p-8"}>
        {rows.length === 0 ? (
          <EmptyState title="No PPM schedules" description="Create recurring inspection schedules per chiller / AHU / VRF. Overdue visits show in red, upcoming ones in amber." />
        ) : (
          <div className="bg-white border border-slate-200 rounded-sm" data-testid="ppm-table">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="label-caps">Title</TableHead>
                  <TableHead className="label-caps">Frequency</TableHead>
                  <TableHead className="label-caps">Equipment</TableHead>
                  <TableHead className="label-caps">Project</TableHead>
                  <TableHead className="label-caps">Next Due</TableHead>
                  <TableHead className="label-caps">Last Done</TableHead>
                  <TableHead className="label-caps">Visits</TableHead>
                  <TableHead className="label-caps">Assignee</TableHead>
                  <TableHead className="w-24" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id} data-testid={`ppm-row-${r.id}`}>
                    <TableCell className="font-medium">{r.title}</TableCell>
                    <TableCell><span className="text-[10px] px-1.5 py-0.5 bg-sky-50 text-sky-700 uppercase tracking-widest rounded-sm">{FREQ_LABEL[r.frequency]}</span></TableCell>
                    <TableCell className="text-[11px] text-slate-600">{eqMap[r.equipment_id] || "—"}</TableCell>
                    <TableCell className="text-slate-600 text-xs">{projMap[r.project_id] || "—"}</TableCell>
                    <TableCell className={`font-mono-num ${dueTone(r.next_due_date)}`}>{fmtDate(r.next_due_date) || "—"}</TableCell>
                    <TableCell className="font-mono-num text-slate-500 text-xs">{fmtDate(r.last_completed_date) || "—"}</TableCell>
                    <TableCell className="font-mono-num text-center">{r.visits_completed}</TableCell>
                    <TableCell className="text-slate-600 text-xs">{r.assignee || "—"}</TableCell>
                    <TableCell>
                      <div className="flex gap-1 justify-end">
                        <button onClick={() => complete(r.id)} className="text-emerald-600 hover:text-emerald-700 p-1" title="Log visit as done" data-testid={`complete-ppm-${r.id}`}><CheckCircle size={14} /></button>
                        <button onClick={() => del(r.id)} className="text-slate-400 hover:text-red-600 p-1"><Trash size={13} /></button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}
